import type { MagazineArticle, MagazineArticleListItem, MagazineIssue, MagazineIssueListItem, Material, MaterialListItem, NewsItem, NewsListItem, ProvaPublica } from '@titularis/api-client';
import { DUVIDAS } from '../conteudo/duvidas.js';
import type { Meta } from './meta.js';
import { tituloDoArtigo } from './citacao.js';
import { DESCRICAO_DA_REVISTA, enderecoDaRevista, LICENCAS_DA_REVISTA, revistaFiltrada, type FiltrosDaRevista } from './revista.js';

/**
 * O texto de cada página pública, num lugar só.
 *
 * **A regra desta pasta: página nova entra aqui.** Enquanto o título e o
 * canônico moravam no `index.html`, toda rota nova nascia dizendo ser a home -
 * e ninguém percebia, porque a página abre igual, o cartão sai igual e só o
 * buscador vê a diferença. Com um construtor por página, esquecer é visível: a
 * rota que não tem construtor não compila no `rotas.ts`.
 *
 * **O que se escreve aqui é o que aparece na busca, e não o que aparece na
 * tela.** São dois textos com dois trabalhos: o `h1` da listagem de provas diz
 * "Provas para imprimir e treinar", porque quem já está na página não precisa
 * ler "de concurso de cartório" de novo; o `<title>` diz "Provas de concurso de
 * cartório em PDF", porque é isso que a pessoa digitou na busca. Divergir é o
 * certo - o errado é o `<title>` repetir a marca e não dizer o assunto.
 *
 * Sem React e sem `@titularis/ui` aqui dentro: o `meta.ts` explica o porquê.
 */

/**
 * O cartão da marca, que serve a toda página sem imagem própria.
 *
 * O caminho é relativo aqui e vira absoluto em cada construtor, colado na
 * origem. **A URL da imagem tem de ser absoluta na tag**: o WhatsApp busca a
 * imagem do servidor dele, sem contexto de página, e caminho relativo
 * simplesmente não resolve - é o erro mais comum nesta tag. A arte e o porquê
 * da composição estão em `scripts/og/README.md`.
 */
const IMAGEM_DA_MARCA = '/og-titularis.jpg';
const ALT_DA_MARCA = 'O logotipo da Titularis e o texto: questoes comentadas para cartorios';

const TITULO_DO_SITE = 'Titularis - questões comentadas para o concurso de Titular de Cartório';

/**
 * O nome de cada fase **na busca**, que não é o nome dela na tela.
 *
 * O `moduleLabel` do `packages/ui` escreve "1ª fase - Cartórios", que é uma
 * etiqueta dentro de uma tabela cheia de contexto. Aqui o texto entra num
 * título de resultado de busca, onde o contexto é a frase inteira: "Provas da
 * 1ª fase de concurso de cartório em PDF". Repetir "Cartórios" no meio dela
 * gastaria a largura que o Google corta.
 *
 * A cópia é intencional, e é a razão de o `packages/ui` não ser importado
 * daqui: são dois textos com dois trabalhos, e não uma duplicação a resolver.
 */
const FASE_NA_BUSCA: Record<string, string> = {
  enac: 'do Enac',
  objective: 'da 1ª fase',
  essay: 'da 2ª fase',
};

/**
 * A mesma fase, agora como rótulo de um link de faceta.
 *
 * Sem preposição porque aqui ela é o texto clicável de uma lista curta, e não
 * um pedaço de frase. Vale para os dois lados: é este rótulo que aparece na
 * tela, no bloco de links do fim da listagem, e no documento que o robô lê.
 */
export const FASE_EM_LINK: Record<string, string> = {
  enac: 'Enac',
  objective: '1ª fase',
  essay: '2ª fase',
};

/** A base de toda meta: cartão da marca, sem data e dentro do índice. */
const base = (origem: string) => ({
  imagem: `${origem}${IMAGEM_DA_MARCA}`,
  imagemAlt: ALT_DA_MARCA,
  tipo: 'website' as const,
});

/**
 * Quem publica, para os dados estruturados.
 *
 * Repetido em toda página de propósito: o buscador junta as ocorrências pelo
 * `@id`, e é assim que ele entende que a notícia, a prova e a landing são do
 * mesmo lugar em vez de três sites parecidos.
 */
const organizacao = (origem: string): Record<string, unknown> => ({
  '@type': 'Organization',
  '@id': `${origem}/#organizacao`,
  name: 'Titularis',
  url: `${origem}/`,
  logo: `${origem}/icone-512.png`,
  description:
    'Plataforma de estudos para o concurso de Titular de Cartório: questões comentadas, legislação comentada e provas para baixar.',
});

/**
 * A trilha até a página, que é o que vira o caminho embaixo do resultado.
 *
 * O Google mostra "titularis.com.br › provas › enac 2026" no lugar da URL crua
 * quando esta trilha existe, e é a diferença entre um resultado que diz onde a
 * pessoa vai cair e um que mostra um endereço com hífen.
 */
const trilha = (
  origem: string,
  degraus: { nome: string; url: string }[],
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [{ nome: 'Início', url: `${origem}/` }, ...degraus].map((degrau, indice) => ({
    '@type': 'ListItem',
    position: indice + 1,
    name: degrau.nome,
    item: degrau.url,
  })),
});

/** Uma lista de links, para o buscador saber o que a listagem contém. */
const lista = (nome: string, itens: { nome: string; url: string }[]): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: nome,
  numberOfItems: itens.length,
  itemListElement: itens.map((item, indice) => ({
    '@type': 'ListItem',
    position: indice + 1,
    name: item.nome,
    url: item.url,
  })),
});

/* ------------------------------------------------------------------ landing */

export const metaDaLanding = (origem: string): Meta => ({
  ...base(origem),
  titulo: TITULO_DO_SITE,
  descricao:
    'Questões do Enac e da 1ª fase de cartórios comentadas com legislação, jurisprudência e doutrina, mais leis comentadas artigo por artigo.',
  url: `${origem}/`,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      ...organizacao(origem),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${origem}/#site`,
      name: 'Titularis',
      url: `${origem}/`,
      inLanguage: 'pt-BR',
      publisher: { '@id': `${origem}/#organizacao` },
      /*
       * A busca declarada é a das provas, e não uma busca do site inteiro: é a
       * única que existe de verdade, com endereço próprio e resultado público.
       * Declarar uma que não existe é o erro que o Google chama de marcação
       * enganosa - e a punição não é perder o recurso, é perder a confiança na
       * marcação toda.
       */
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${origem}/provas?termo={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: DUVIDAS.map((duvida) => ({
        '@type': 'Question',
        name: duvida.pergunta,
        acceptedAnswer: { '@type': 'Answer', text: duvida.resposta },
      })),
    },
  ],
});

/* ----------------------------------------------------------------- notícias */

export const metaDasNoticias = (origem: string, noticias: NewsListItem[] = []): Meta => ({
  ...base(origem),
  titulo: 'Notícias para o concurso de Titular de Cartório',
  /*
   * A mesma frase que a página imprime, mais a linha que ganha o clique.
   *
   * **A meta acompanhou a chamada porque a chamada mudou de escopo**, e não
   * por simetria: a anterior prometia só o que muda na legislação e na
   * jurisprudência, e a seção publica também a notícia do concurso em si -
   * lista de vacância, edital, prazo. Descrição mais estreita que o conteúdo
   * é o buscador mandando a pessoa errada, ou não mandando a certa.
   */
  descricao:
    'Notícias dos concursos de cartórios, atualizações legislativas e temas relevantes. Aberto para todo mundo, sem conta e sem cadastro.',
  url: `${origem}/noticias`,
  jsonLd: [
    trilha(origem, [{ nome: 'Notícias', url: `${origem}/noticias` }]),
    lista(
      'Notícias recentes',
      noticias.map((noticia) => ({
        nome: noticia.title,
        url: `${origem}/noticias/${noticia.slug}`,
      })),
    ),
  ],
});

/**
 * A meta de uma notícia.
 *
 * A imagem sai de `/news/{slug}/card`, e não de `/news/{slug}/image`: a capa
 * crua tem a medida que o editor subiu, e abaixo de 600x315 o cartão grande
 * degenera em miniatura. O `cartao.ts` da API explica o resto.
 *
 * **Notícia sem capa cai no cartão da marca**, e não num retângulo cinza. É a
 * mesma escolha do fallback de erro do `rotas.ts`: prévia alguma é pior que uma
 * prévia genérica, porque o grupo lê ausência de cartão como link suspeito.
 */
export const metaDaNoticia = (noticia: NewsItem, origem: string, urlDaApi: string): Meta => {
  const url = `${origem}/noticias/${noticia.slug}`;
  const imagem = noticia.temImagem
    ? `${urlDaApi}/news/${encodeURIComponent(noticia.slug)}/card`
    : `${origem}${IMAGEM_DA_MARCA}`;

  return {
    titulo: noticia.title,
    descricao: noticia.excerpt,
    url,
    imagem,
    imagemAlt: noticia.temImagem ? `Capa da notícia: ${noticia.title}` : ALT_DA_MARCA,
    tipo: 'article',
    publicadaEm: noticia.publishedAt,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: noticia.title,
        description: noticia.excerpt,
        image: [imagem],
        datePublished: noticia.publishedAt,
        /*
         * Sem `dateModified` inventado: repetir a data de publicação aqui
         * diria ao buscador que a notícia foi revisada quando ela não foi. O
         * contrato público não expõe a data de edição, e campo ausente é
         * melhor que campo errado.
         */
        inLanguage: 'pt-BR',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        /*
         * **Quem assina é a casa, e não a pessoa.** A API devolve o
         * `authorName`, e por ora o site não o mostra em lugar nenhum: a
         * escolha é não expor o time enquanto a redação é pequena. Deixar
         * o nome só aqui seria pior que mostrá-lo na página, porque ele
         * chegaria ao buscador sem chegar a quem lê.
         */
        author: organizacao(origem),
        /*
         * A organização entra inteira, e não como referência ao `@id` da
         * landing. Um `@id` só resolve dentro do grafo da **mesma** página: na
         * notícia, a referência sozinha apontaria para um nó que não existe
         * ali, e quem publica ficaria em branco. Com o objeto inteiro, o `@id`
         * segue servindo para o buscador juntar as ocorrências.
         */
        publisher: organizacao(origem),
      },
      trilha(origem, [
        { nome: 'Notícias', url: `${origem}/noticias` },
        { nome: noticia.title, url },
      ]),
    ],
  };
};

/* -------------------------------------------------------------------- provas */

export interface FiltrosDaListagem {
  termo?: string;
  uf?: string;
  ano?: string;
  modulo?: string;
  pagina?: number;
}

/**
 * O endereço canônico de uma listagem filtrada.
 *
 * **A ordem dos filtros é fixa, e é o ponto.** `?ano=2025&uf=SP` e
 * `?uf=SP&ano=2025` abrem a mesma página, e sem uma ordem canônica o buscador
 * as trata como duas - divide entre elas o que a página conquistou e não
 * indexa nem uma nem outra direito.
 *
 * `pagina=1` sai do endereço porque é o mesmo lugar que `/provas`, e o `termo`
 * fica de fora porque busca não é faceta: cada palavra digitada criaria um
 * endereço, e nenhum deles é uma página que alguém procurou.
 */
export const enderecoDasProvas = (origem: string, filtros: FiltrosDaListagem = {}): string => {
  const busca = new URLSearchParams();
  if (filtros.uf) busca.set('uf', filtros.uf);
  if (filtros.ano) busca.set('ano', filtros.ano);
  if (filtros.modulo) busca.set('modulo', filtros.modulo);
  if (filtros.pagina && filtros.pagina > 1) busca.set('pagina', String(filtros.pagina));

  const consulta = busca.toString();
  return `${origem}/provas${consulta ? `?${consulta}` : ''}`;
};

/**
 * O título de uma listagem de provas, montado a partir do recorte.
 *
 * Cada faceta vira uma frase que alguém digita: "provas de cartório de SP",
 * "provas do Enac 2025". Sem isso as facetas seriam vinte endereços com o
 * mesmo título, que é a definição de conteúdo duplicado.
 *
 * **A UF entra como sigla porque é o que temos.** O nome do estado mora no
 * `packages/ui`, que não pode ser importado daqui (ver `meta.ts`), e a API não
 * devolve o nome junto da sigla. Quando a primeira prova estadual for
 * publicada, é aqui que "SP" vira "São Paulo" - e o caminho barato é a API
 * mandar o nome no `/papers/filters`.
 */
const tituloDasProvas = (filtros: FiltrosDaListagem): string => {
  const fase = filtros.modulo ? FASE_NA_BUSCA[filtros.modulo] : undefined;
  const partes = [
    'Provas',
    fase ?? 'de concurso de cartório',
    filtros.uf ? `de ${filtros.uf}` : undefined,
    filtros.ano,
    'para baixar em PDF',
  ].filter(Boolean);

  return partes.join(' ');
};

export const metaDasProvas = (
  origem: string,
  filtros: FiltrosDaListagem = {},
  provas: ProvaPublica[] = [],
  /**
   * Quantas provas o recorte tem, **quando já se sabe**.
   *
   * Sem valor não é o mesmo que zero: a tela chama isto antes de a API
   * responder, e tratar "ainda não sei" como "não achei nada" marcaria
   * `noindex` em toda listagem no meio segundo do carregamento.
   */
  total?: number,
): Meta => {
  const filtrada = Boolean(filtros.uf ?? filtros.ano ?? filtros.modulo ?? filtros.termo);

  return {
    ...base(origem),
    titulo: tituloDasProvas(filtros),
    descricao: filtrada
      ? `Provas, gabaritos e editais deste recorte, em PDF e de graça. Abre sem conta, imprime em uma folha e serve para treinar no papel.`
      : 'Provas, gabaritos e editais do Enac e dos concursos de cartório de todo o Brasil. Baixe em PDF, de graça e sem conta, e treine no papel do jeito que a prova é.',
    url: enderecoDasProvas(origem, filtros),
    /*
     * Duas páginas ficam fora do índice, e por motivos diferentes: a **busca
     * por termo**, porque resultado de busca interna indexado é página que só
     * existe porque alguém digitou; e o **filtro sem resultado**, porque uma
     * listagem vazia não responde à pergunta que trouxe a pessoa e ainda ensina
     * o buscador que o site tem páginas vazias.
     */
    foraDoIndice: Boolean(filtros.termo) || (filtrada && total === 0),
    jsonLd: [
      trilha(origem, [{ nome: 'Provas', url: `${origem}/provas` }]),
      lista(
        'Provas para imprimir',
        provas.map((prova) => ({ nome: prova.name, url: `${origem}/provas/${prova.slug}` })),
      ),
    ],
  };
};

/**
 * Os arquivos da prova, escritos como se fala.
 *
 * O texto anterior dizia "Baixe **o** prova e gabarito", que sai no cartão do
 * WhatsApp e é a primeira coisa que o lead lê. Cada arquivo carrega o próprio
 * artigo, e o "e" fica só antes do último.
 */
export const arquivosDaProva = (prova: ProvaPublica): string[] =>
  [
    prova.temProva ? 'a prova' : null,
    prova.temGabarito ? 'o gabarito' : null,
    prova.temEdital ? 'o edital' : null,
  ].filter((arquivo): arquivo is string => arquivo !== null);

export const listarArquivos = (arquivos: string[]): string =>
  arquivos.length > 1
    ? `${arquivos.slice(0, -1).join(', ')} e ${arquivos.at(-1)}`
    : (arquivos[0] ?? 'os arquivos');

/**
 * A ficha da prova em uma frase.
 *
 * É o texto que a página da prova não tinha: antes dela o documento inteiro era
 * o nome do concurso, três etiquetas e um PDF dentro de um `<iframe>` - e o
 * PDF, servido como `blob:`, o buscador não lê. Uma página com quarenta
 * palavras não disputa "prova do Enac 2026 pdf" com quem escreveu um parágrafo.
 */
export const fichaDaProva = (prova: ProvaPublica): string => {
  const fase = FASE_NA_BUSCA[prova.module] ?? 'de concurso de cartório';
  const uf = prova.state ? ` (${prova.state})` : '';

  /*
   * A banca fica de fora, e ela é um termo de busca ("prova da FGV para
   * cartório"). O que decide é a largura: a descrição da página soma esta
   * frase à do leitor, e o Google corta o que passa de ~155 caracteres. O nome
   * da banca aparece na ficha visível da página e no `creator` do dado
   * estruturado, que é onde ele não disputa espaço com o resto.
   */
  return `Baixe ${listarArquivos(arquivosDaProva(prova))} ${fase} de ${prova.year}${uf} em PDF, de graça.`;
};

export const metaDaProva = (prova: ProvaPublica, origem: string): Meta => {
  const url = `${origem}/provas/${prova.slug}`;
  const arquivos = arquivosDaProva(prova).map((arquivo) => arquivo.replace(/^[ao] /, ''));

  return {
    ...base(origem),
    /*
     * O nome do concurso vem primeiro porque é o que a pessoa digitou; o que
     * vem depois - "prova e gabarito em PDF" - é o que decide o clique entre
     * dois resultados com o mesmo nome de concurso.
     */
    titulo: `${prova.name}: ${listarArquivos(arquivos)} em PDF`,
    descricao: `${fichaDaProva(prova)} Abre no navegador, imprime em uma folha e serve para treinar no papel.`,
    url,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: prova.name,
        description: fichaDaProva(prova),
        url,
        learningResourceType: 'Prova de concurso',
        educationalLevel: 'Concurso público para Titular de Cartório',
        inLanguage: 'pt-BR',
        encodingFormat: 'application/pdf',
        isAccessibleForFree: true,
        /*
         * `temporalCoverage`, e não `datePublished`: o que se sabe é o ano em
         * que a prova foi aplicada, e `Date` no schema.org quer data completa.
         * Ano solto naquele campo é o tipo de coisa que o validador do Google
         * marca em amarelo sem que ninguém veja.
         */
        temporalCoverage: String(prova.year),
        provider: organizacao(origem),
        ...(prova.boardName ? { creator: { '@type': 'Organization', name: prova.boardName } } : {}),
      },
      trilha(origem, [
        { nome: 'Provas', url: `${origem}/provas` },
        { nome: prova.name, url },
      ]),
    ],
  };
};

/* ------------------------------------------------------------------ revista */

export const metaDaRevista = (
  origem: string,
  edicoes: MagazineIssueListItem[] = [],
  filtros: FiltrosDaRevista = {},
  artigos: MagazineArticleListItem[] = [],
  total?: number,
): Meta => {
  const filtrada = revistaFiltrada(filtros);
  const recorte = [filtros.keyword, filtros.ano, filtros.edicao, (filtros.pagina ?? 1) > 1 ? `página ${filtros.pagina}` : null].filter(Boolean).join(' · ');
  return {
    ...base(origem),
    titulo: `Revista Titularis${recorte ? `: ${recorte}` : ''}`,
    descricao: DESCRICAO_DA_REVISTA,
    url: enderecoDaRevista(origem, filtros),
    foraDoIndice: Boolean(filtros.termo) || (filtrada && total === 0),
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'CollectionPage',
        name: 'Revista Titularis', url: enderecoDaRevista(origem, filtros),
        mainEntity: lista('Revista Titularis', filtrada
          ? artigos.map((artigo) => ({ nome: tituloDoArtigo(artigo), url: `${origem}/revista/artigo/${encodeURIComponent(artigo.slug)}` }))
          : edicoes.map((edicao) => ({ nome: edicao.label, url: `${origem}/revista/${encodeURIComponent(edicao.slug)}` }))),
      },
      trilha(origem, [{ nome: 'Revista', url: `${origem}/revista` }]),
    ],
  };
};

const edicaoEstruturada = (edicao: MagazineArticle['issue'], origem: string) => ({
  '@type': 'PublicationIssue', name: edicao.label, issueNumber: edicao.number,
  url: `${origem}/revista/${encodeURIComponent(edicao.slug)}`,
  isPartOf: {
    '@type': 'PublicationVolume', volumeNumber: edicao.volume,
    isPartOf: { '@type': 'Periodical', name: 'Revista Titularis', url: `${origem}/revista` },
  },
});

export const metaDaEdicao = (edicao: MagazineIssue, origem: string, urlDaApi: string): Meta => ({
  ...base(origem),
  titulo: `Revista Titularis: ${edicao.label}`,
  descricao: `Sumário da Revista Titularis, ${edicao.label}. Artigos, autores e leitura em PDF.`,
  url: `${origem}/revista/${encodeURIComponent(edicao.slug)}`,
  ...(edicao.temCapa ? {
    imagem: `${urlDaApi}/magazine/issues/${encodeURIComponent(edicao.slug)}/cover`,
    imagemAlt: `Capa da Revista Titularis, ${edicao.label}`,
  } : {}),
  jsonLd: [
    {
      '@context': 'https://schema.org', ...edicaoEstruturada(edicao, origem),
      datePublished: edicao.publishedAt,
      hasPart: edicao.artigos.map((artigo) => ({
        '@type': 'ScholarlyArticle', headline: tituloDoArtigo(artigo),
        url: `${origem}/revista/artigo/${encodeURIComponent(artigo.slug)}`,
      })),
    },
    trilha(origem, [{ nome: 'Revista', url: `${origem}/revista` }, { nome: edicao.label, url: `${origem}/revista/${encodeURIComponent(edicao.slug)}` }]),
  ],
});

export const metaDoArtigo = (artigo: MagazineArticle, origem: string, urlDaApi: string): Meta => {
  const url = `${origem}/revista/artigo/${encodeURIComponent(artigo.slug)}`;
  // Sem capa, a rota /card responde 404. A marca mantém a prévia legível nesse caso.
  const imagem = artigo.temCapa
    ? `${urlDaApi}/magazine/articles/${encodeURIComponent(artigo.slug)}/card`
    : `${origem}${IMAGEM_DA_MARCA}`;
  return {
    titulo: tituloDoArtigo(artigo), descricao: artigo.excerpt,
    url, imagem, imagemAlt: artigo.temCapa ? `Capa do artigo: ${artigo.title}` : ALT_DA_MARCA,
    tipo: 'article', publicadaEm: artigo.publishedAt,
    jsonLd: [
      {
        '@context': 'https://schema.org', '@type': 'ScholarlyArticle',
        headline: tituloDoArtigo(artigo), author: artigo.authors.map((autor) => ({
          '@type': 'Person', name: autor.name,
          ...(autor.affiliation ? { affiliation: { '@type': 'Organization', name: autor.affiliation } } : {}),
        })),
        datePublished: artigo.publishedAt, abstract: artigo.abstract, keywords: artigo.keywords,
        license: LICENCAS_DA_REVISTA[artigo.license].url,
        isPartOf: edicaoEstruturada(artigo.issue, origem),
        image: [imagem], inLanguage: 'pt-BR', isAccessibleForFree: true,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url }, publisher: organizacao(origem),
      },
      trilha(origem, [
        { nome: 'Revista', url: `${origem}/revista` },
        { nome: artigo.issue.label, url: `${origem}/revista/${encodeURIComponent(artigo.issue.slug)}` },
        { nome: artigo.title, url },
      ]),
    ],
  };
};

/* ---------------------------------------------------------------- materiais */

/**
 * O que a lista de materiais diz de si, na busca e na prévia.
 *
 * É também o endereço impresso no rodapé de todo material ("versão atualizada
 * em titularis.com.br/materiais"), então a descrição fala com quem digitou o
 * endereço de um PDF que recebeu num grupo.
 */
export const DESCRICAO_DOS_MATERIAIS =
  'Cronogramas, resumos e mapas para o concurso de Titular de Cartório, de graça e em PDF, com as questões comentadas de cada tema.';

export const metaDosMateriais = (origem: string, materiais: MaterialListItem[] = []): Meta => ({
  ...base(origem),
  titulo: 'Materiais grátis para o concurso de cartório',
  descricao: DESCRICAO_DOS_MATERIAIS,
  url: `${origem}/materiais`,
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Materiais grátis',
      url: `${origem}/materiais`,
      mainEntity: lista(
        'Materiais grátis',
        materiais.map((material) => ({
          nome: material.title,
          url: `${origem}/materiais/${encodeURIComponent(material.slug)}`,
        })),
      ),
    },
    trilha(origem, [{ nome: 'Materiais', url: `${origem}/materiais` }]),
  ],
});

export const metaDoMaterial = (material: Material, origem: string, urlDaApi: string): Meta => {
  const url = `${origem}/materiais/${encodeURIComponent(material.slug)}`;
  /*
   * O cartão é a capa remontada em 1200x630 pela API. Sem capa, a rota
   * responde 404 - e link sem imagem num grupo parece golpe -, então a marca
   * entra no lugar.
   */
  const imagem = material.temCapa
    ? `${urlDaApi}/materials/${encodeURIComponent(material.slug)}/card`
    : `${origem}${IMAGEM_DA_MARCA}`;

  return {
    titulo: material.title,
    descricao: material.summary,
    url,
    imagem,
    imagemAlt: material.temCapa ? `Capa do material: ${material.title}` : ALT_DA_MARCA,
    tipo: 'article',
    publicadaEm: material.publishedAt,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: material.title,
        description: material.summary,
        url,
        image: [imagem],
        learningResourceType: 'Material de estudo',
        educationalLevel: 'Concurso público para Titular de Cartório',
        inLanguage: 'pt-BR',
        encodingFormat: 'application/pdf',
        isAccessibleForFree: true,
        datePublished: material.publishedAt,
        ...(material.fileUpdatedAt ? { dateModified: material.fileUpdatedAt } : {}),
        provider: organizacao(origem),
      },
      trilha(origem, [
        { nome: 'Materiais', url: `${origem}/materiais` },
        { nome: material.title, url },
      ]),
    ],
  };
};

/* ------------------------------------------------------------ não encontrado */

/**
 * O endereço que não existe.
 *
 * **Ele precisa dizer que não existe.** Até aqui qualquer endereço torto
 * respondia 200 com a landing inteira, e o buscador chama isso de soft 404: ele
 * indexa dezenas de endereços inventados como se fossem a home, e passa a
 * desconfiar do que o site responde. O `noindex` é a metade que o navegador vê;
 * a outra é o status 404 que a função da Vercel devolve ao robô.
 */
export const metaDeNaoEncontrado = (origem: string, caminho = '/'): Meta => ({
  ...base(origem),
  titulo: 'Página não encontrada',
  descricao: 'Este endereço não existe mais, ou nunca existiu.',
  url: `${origem}${caminho}`,
  foraDoIndice: true,
});

/**
 * O descadastro, fora do índice.
 *
 * Não é conteúdo: é uma ação que só faz sentido para quem chegou pelo link de
 * uma mensagem, com o token na mão. Indexada, apareceria na busca por
 * "titularis" como se fosse uma porta do produto - e o endereço sem token não
 * responde nada útil a ninguém.
 */
export const metaDoDescadastro = (origem: string): Meta => ({
  ...base(origem),
  titulo: 'Sair da lista',
  descricao: 'Cancele o recebimento das mensagens da Titularis.',
  url: `${origem}/descadastro`,
  foraDoIndice: true,
});

/**
 * Os dois documentos legais, **dentro** do índice.
 *
 * Ao contrário do descadastro, eles não são uma ação: são o que alguém procura
 * de propósito, quase sempre antes de digitar o número do cartão. Página de
 * termos que só existe depois do JavaScript é página que, para quem confere se
 * a loja é séria, não existe.
 *
 * A descrição diz o que o documento resolve, e não "estes são os termos": quem
 * lê o resultado da busca está decidindo se clica.
 */
export const metaDosTermos = (origem: string): Meta => ({
  ...base(origem),
  titulo: 'Termos de Uso',
  descricao:
    'As regras da Titularis: o que o módulo dá, como se paga, o prazo de sete dias para desistir e o que não se pode fazer com o conteúdo.',
  url: `${origem}/termos`,
});

export const metaDaPrivacidade = (origem: string): Meta => ({
  ...base(origem),
  titulo: 'Política de Privacidade',
  descricao:
    'Quais dados a Titularis coleta, por que coleta, com quem compartilha, por quanto tempo guarda e como você exerce os seus direitos.',
  url: `${origem}/privacidade`,
});
